import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert } from './ui/alert';
import { Users, Globe, Lock, CheckCircle } from 'lucide-react';
import { Assistant } from '../types/assistant';
import { User } from '../types/user';
import { useToast } from '../hooks/use-toast';

interface AssistantSharingModalProps {
  assistant: Assistant;
  currentUser: User;
  tenantUsers: User[];
  isBusinessAccount: boolean;
  onSave: (visibility: 'private' | 'team' | 'shared', sharedWithUsers: string[]) => Promise<void>;
  onClose: () => void;
}

export function AssistantSharingModal({
  assistant,
  currentUser,
  tenantUsers,
  isBusinessAccount,
  onSave,
  onClose
}: AssistantSharingModalProps) {
  const [visibility, setVisibility] = useState<'private' | 'team' | 'shared'>(
    assistant.visibility || 'private'
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>(
    assistant.sharedWithUsers || []
  );
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Debug
  useEffect(() => {
    console.log('🎨 Modal de compartilhamento aberto');
    console.log('👤 Agente:', assistant);
    console.log('👥 Usuários disponíveis:', tenantUsers);
    console.log('💼 É Business?', isBusinessAccount);
  }, []);

  // Filtrar usuários (excluir o criador)
  const availableUsers = tenantUsers.filter(u => u.id !== assistant.createdBy);

  const handleToggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Se for team ou private, limpar seleção de usuários
      const usersToShare = visibility === 'shared' ? selectedUsers : [];
      
      await onSave(visibility, usersToShare);
      
      // Fechar modal primeiro
      onClose();
      
      // Toast depois de fechar
      setTimeout(() => {
        toast({
          title: 'Sucesso!',
          description: 'Configurações de compartilhamento salvas.',
        });
      }, 100);
    } catch (error: any) {
      console.error('Erro ao salvar compartilhamento:', error);
      setIsSaving(false);
      toast({
        title: 'Erro',
        description: error?.message || 'Erro ao salvar configurações',
        variant: 'destructive',
      });
    }
  };

  // Se não for conta business, não permitir compartilhamento
  if (!isBusinessAccount) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Compartilhamento Indisponível</DialogTitle>
            <DialogDescription>
              O compartilhamento de agentes está disponível apenas para contas Business.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-6">
        <DialogHeader className="mb-6">
          <DialogTitle>Compartilhar Agente</DialogTitle>
          <DialogDescription>
            Configure quem pode acessar "{assistant.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mb-6">
          {/* Seletor de Visibilidade */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-900">Visibilidade</Label>
            
            {/* Privado */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                visibility === 'private'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setVisibility('private')}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {visibility === 'private' ? (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-900">Privado</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Apenas você tem acesso a este agente
                  </p>
                </div>
              </div>
            </div>

            {/* Toda Empresa */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                visibility === 'team'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setVisibility('team')}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {visibility === 'team' ? (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-900">Toda Empresa</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Todos os usuários da empresa podem acessar
                  </p>
                </div>
              </div>
            </div>

            {/* Usuários Específicos */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                visibility === 'shared'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setVisibility('shared')}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {visibility === 'shared' ? (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="font-medium text-gray-900">Usuários Específicos</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Escolha quem pode acessar
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Lista de Usuários (apenas quando visibility = 'shared') */}
          {visibility === 'shared' && (
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-900">
                Selecionar Usuários ({selectedUsers.length} selecionados)
              </Label>
              
              {availableUsers.length === 0 ? (
                <Alert>
                  <p className="text-sm text-gray-600">
                    Não há outros usuários na empresa para compartilhar.
                  </p>
                </Alert>
              ) : (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleToggleUser(user.id)}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={() => handleToggleUser(user.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {user.role === 'manager' ? 'Gestor' : 'Usuário'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aviso */}
          {visibility === 'shared' && selectedUsers.length === 0 && (
            <Alert className="bg-yellow-50 border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ Nenhum usuário selecionado. O agente ficará privado até que você selecione usuários.
              </p>
            </Alert>
          )}
        </div>

        <DialogFooter className="mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
            {isSaving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

